import React, { useState, useEffect } from 'react';
import GameCard from '../../components/GameCard';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import EmptyState from '../../components/EmptyState';
import { PlatformIconComponent } from '../../components/icons/PlatformIcons';
import styles from './GameGrid.module.css';

const PAGE_SIZE = 8;

function formatComingSoonDate(isoDate) {
  if (!isoDate) return 'Coming Soon';
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return 'Coming Soon';
  return 'Free from ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function GameGrid({ games, loading, error, activePlatform, isDemoMode, isClaimed, toggleClaimed, isWishlisted, toggleWishlist }) {
  const [page, setPage] = useState(1);

  // Reset to page 1 whenever the tab/platform changes
  useEffect(() => { setPage(1); }, [activePlatform]);

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <LoadingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.errorBanner}>
          <div className={styles.errorIndicator} />
          <div>
            <p className={styles.errorTitle}>Could not load games</p>
            <p className={styles.errorHint}>Check your network connection and try refreshing.</p>
          </div>
        </div>
      </div>
    );
  }

  const liveGames    = (games || []).filter(g => g.type !== 'coming-soon');
  const upcomingGames = (games || []).filter(g => g.type === 'coming-soon');
  const hasLive      = liveGames.length > 0;
  const totalPages   = Math.ceil(liveGames.length / PAGE_SIZE);
  const pagedGames   = liveGames.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className={styles.wrapper}>
      {isDemoMode && (
        <div className={styles.demoBanner}>
          <span className={styles.demoLabel}>Preview Mode</span>
          <span className={styles.demoText}>
            Showing sample data. Set <code>REACT_APP_GAS_URL</code> to load live data.
          </span>
        </div>
      )}

      {!hasLive ? (
        <EmptyState platform={activePlatform} />
      ) : (
        <>
          <div className={styles.grid}>
            {pagedGames.map(game => (
              <GameCard
                key={game.id}
                game={game}
                claimed={isClaimed(game.id)}
                onToggleClaimed={() => toggleClaimed(game.id)}
                wishlisted={isWishlisted(game.id)}
                onToggleWishlist={() => toggleWishlist(game)}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                disabled={page === 1}
                onClick={() => { setPage(p => p - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                aria-label="Previous page"
              >
                ← Prev
              </button>
              <span className={styles.pageInfo}>{page} / {totalPages}</span>
              <button
                className={styles.pageBtn}
                disabled={page === totalPages}
                onClick={() => { setPage(p => p + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                aria-label="Next page"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Coming Soon strip — only shown on All tab or Epic tab */}
      {upcomingGames.length > 0 && (
        <div className={styles.comingSoonSection}>
          <div className={styles.comingSoonHeader}>
            <span className={styles.comingSoonDot} />
            <span className={styles.comingSoonTitle}>Coming Soon — Epic Games</span>
          </div>
          <div className={styles.comingSoonGrid}>
            {upcomingGames.map(game => (
              <ComingSoonCard key={game.id} game={game} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ComingSoonCard({ game }) {
  const [imgError, setImgError] = useState(false);

  return (
    <a
      href={game.claimUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.comingSoonCard}
      aria-label={`${game.title} — coming soon on Epic`}
    >
      {!imgError && game.thumbnail ? (
        <img
          src={game.thumbnail}
          alt={game.title}
          className={styles.comingSoonThumb}
          onError={() => setImgError(true)}
        />
      ) : (
        <div className={styles.comingSoonThumbFallback}>
          <PlatformIconComponent platform={game.platform} size={28} />
        </div>
      )}
      <div className={styles.comingSoonInfo}>
        <div className={styles.comingSoonLabel}>Coming Soon</div>
        <div className={styles.comingSoonName}>{game.title}</div>
        <div className={styles.comingSoonWhen}>
          {formatComingSoonDate(game.availableFrom)}
        </div>
      </div>
    </a>
  );
}

export { GameGrid };
export default GameGrid;

